import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  X, Car, FileText, Phone, Mail, MessageCircle, AlertTriangle, MapPin, RefreshCw, Pencil, Trash2, Paperclip, ExternalLink, Users, Plus, PhoneCall,
} from "lucide-react";
import { money, money2, daysLeft, calcCommission, effectiveRate } from "../lib/finance";
import { API } from "../lib/apiClient";
import { waTarget } from "../lib/wa";
import { Modal, Empty } from "./common";
import { normalizePlate, isValidPlate, plateToProvince } from "../lib/plate";

export function VehicleDetail({ group, branches, settings, onClose, onEditPolicy, onFollowUp, onEditManual, onDeleteManual, onAddPolicy }) {
  // Group policies by branch
  const byBranch = {};
  group.policies.forEach((p) => {
    const b = p.branch || "(Belirtilmemiş)";
    if (!byBranch[b]) byBranch[b] = [];
    byBranch[b].push(p);
  });

  const today = new Date().toISOString().slice(0, 10);
  const custList = Array.from(group.customers);
  const activePolicies = group.policies.filter((p) => p.start <= today && p.end >= today);

  return (
    <div className="modal-backdrop" data-testid="vehicle-detail">
      <div className="modal" style={{ maxWidth: 780 }}>
        <div className="modal-head">
          <div>
            <div className="vehicle-plate-badge large">
              <span className="plate-country">TR</span>
              <span className="plate-number">{group.plate}</span>
            </div>
            <div className="vehicle-owner" style={{ marginTop: 10 }}>
              <Users size={13} /> {custList.join(", ") || <em>Sahibi belirtilmedi</em>}
            </div>
            {(group.brand || group.model || group.year) && (
              <div className="vehicle-owner" style={{ marginTop: 4 }} data-testid="vehicle-detail-brand">
                <Car size={13} /> {[group.brand, group.model, group.year].filter(Boolean).join(" ")}
              </div>
            )}
            {group.ruhsatSeriNo && (
              <div className="vehicle-owner" style={{ marginTop: 4 }} data-testid="vehicle-detail-ruhsat">
                <FileText size={13} /> Ruhsat Seri No: <b style={{ letterSpacing: 1 }}>{group.ruhsatSeriNo}</b>
              </div>
            )}
            {group.note && (
              <div className="vehicle-owner" style={{ marginTop: 4, fontStyle: "italic" }}>
                {group.note}
              </div>
            )}
          </div>
          <button className="icon-button" data-testid="vehicle-detail-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="vehicle-detail-actions">
          <button className="outline-button" data-testid="vehicle-add-policy" onClick={onAddPolicy}><Plus size={14} /> Bu araca poliçe ekle</button>
          {onEditManual && <button className="outline-button" data-testid="vehicle-edit-manual" onClick={onEditManual}><Pencil size={14} /> Araç bilgilerini düzenle</button>}
          {onDeleteManual && <button className="outline-button danger" data-testid="vehicle-delete-manual" onClick={onDeleteManual}><Trash2 size={14} /> Envanterden sil</button>}
        </div>

        <div className="vehicle-summary">
          <div><strong>{group.policies.length}</strong><span>Toplam poliçe</span></div>
          <div><strong>{activePolicies.length}</strong><span>Aktif poliçe</span></div>
          <div><strong>{money(group.totalPremium)}</strong><span>Toplam prim</span></div>
          <div className="commission"><strong>{money(group.totalPersonal)}</strong><span>Toplam hak ediş</span></div>
        </div>

        {group.missingCoverage.length > 0 && (
          <div className="vehicle-warning" data-testid="vehicle-warning">
            <AlertTriangle size={14} />
            <div>
              <strong>Eksik teminat:</strong> Bu araç için aktif <b>{group.missingCoverage.join(" ve ")}</b> poliçesi bulunmuyor. Müşteriyle iletişime geçmek isteyebilirsiniz.
            </div>
          </div>
        )}

        <div className="vehicle-branches">
          {Object.keys(byBranch).length === 0 ? (
            <Empty text="Bu araç için henüz poliçe yok. 'Bu araca poliçe ekle' ile ilk poliçeyi ekleyebilirsiniz." />
          ) : Object.entries(byBranch).map(([branch, list]) => (
            <div key={branch} className="vehicle-branch-block" data-testid={`vehicle-branch-${branch}`}>
              <h4><span className="branch-pill">{branch}</span> <em>{list.length} poliçe</em></h4>
              <div className="vehicle-timeline">
                {list.map((p) => {
                  const isActive = p.start <= today && p.end >= today;
                  const isExpired = p.end < today;
                  const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null);
                  return (
                    <div key={p.id} className={`vehicle-timeline-row ${isActive ? "active" : isExpired ? "expired" : "upcoming"}`}>
                      <div className="vt-status">
                        {isActive ? <span className="dot active" title="Aktif" /> : isExpired ? <span className="dot expired" title="Süresi doldu" /> : <span className="dot upcoming" title="Gelecek" />}
                      </div>
                      <div className="vt-info">
                        <strong>{p.policeNo || "—"}</strong>
                        <span>{p.company || "—"}</span>
                        <small>{p.start} — {p.end}</small>
                      </div>
                      <div className="vt-amounts">
                        <strong>{money(p.premium || 0)}</strong>
                        <span className="commission">{money(c.personal)}</span>
                      </div>
                      <div className="vt-actions">
                        <button className="row-action" title="Düzenle" data-testid={`vehicle-edit-${p.id}`} onClick={() => onEditPolicy(p)}><Pencil size={13} /></button>
                        <button className="row-action" title="Takip notu ekle" data-testid={`vehicle-fu-${p.id}`} onClick={() => onFollowUp(p)}><PhoneCall size={13} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

